// ============================================================================
// CLEO'S SECRET — Shared design tokens
// File: src/lib/design-tokens.js
//
// Single source of truth for palette, typography, and atomic UI primitives.
// Updated May 2026 to incorporate BOTH the landing-page editorial system
// AND the Stage 2 Diagnostic Experience formatting rules.
//
// KEY RULES (Stage 2):
//   • Section labels Cinzel 14px weight 700 letter-spacing 0.32em
//   • Module identifiers stack vertically: numeral → 1px gold rule → day label
//   • Day/duration labels 15-17px weight 700 letter-spacing 0.3em (never 11px)
//   • Small text always weight 600-700 (labels, eyebrows, captions)
//   • Bullets = 9px diamonds rotated 45° BR, 36px left padding, 22px between
//   • Inline italic = BR Cormorant 1.05em; inline bold = INK weight 700
//   • No em dashes in copy — use periods, colons, or commas
//   • PAP page bg, SAND section/card backgrounds, 1px SAND3 borders
//   • Body 16-20px Cormorant lineHeight 1.85 textAlign justify hyphens auto
//   • Content H2 = italic Cormorant 26-34px INK (never all-caps small)
//   • Error pages use editorial typography (italic premise + big title)
//   • Auth-wait loop on direct URL member routes
//
// USAGE:
//   import { P, T, AnkhMed, Eyebrow, SectionDivider, SectionHeadline,
//            PageOpener, ModuleIdentifier, BulletList, ErrorState,
//            Colonnade, BtnPrimary, BtnGhost,
//            SECTION_PADDING, CONTAINER_MAX, CONTAINER_READING,
//            inlineItalic, inlineBold }
//   from '../lib/design-tokens';
// ============================================================================

import React from 'react';

// ───────── FAMILIES ─────────
export const FF     = "'Cormorant Garamond', Georgia, serif";
export const FS     = "system-ui, -apple-system, sans-serif";
export const CINZEL = "'Cinzel', 'Cormorant Garamond', Georgia, serif";

// ───────── PALETTE ─────────
// Canonical plum noir + saturated gold + warm cream per docs/design-spec.md
// Section 2. Old brown/pale-gold tokens are kept as deprecated aliases so no
// importing page breaks at first paint; per-page migration happens in later
// sessions and replaces deprecated names with their canonical counterparts.
export const P = {
  // Cream grounds
  PAP:   '#FAF6EE',
  SAND:  '#E8DDC4',
  SAND2: '#DDD0B0',
  SAND3: '#C8B888',

  // Plum noir — dark grounds (canonical per spec Section 2)
  OBS:        '#1D1116', // primary dark — hero, all dark sections
  OBS_NAV:    '#2A1820', // lifted plum — navigation bar
  OBS_DEEP:   '#15090D', // dropdowns, recessed surfaces
  OBS_LIT:    '#3A212C', // hero centre-glow stop
  OBS_SHADOW: '#0F080C', // vignette corners (plum, not black)

  // DEPRECATED — repoint to closest canonical plum so currently-importing
  // surfaces (ClinicalDashboard, AdminPanel) keep rendering legibly.
  OBS2:  '#2A1820', // DEPRECATED — use OBS_NAV
  OBS3:  '#3A212C', // DEPRECATED — use OBS_LIT

  // Golds — saturated for plum pairing
  GO:    '#C9A961', // primary gold — headlines, CTAs, hairlines, diamonds
  GO2:   '#E0C685', // light gold — italic emphasis on dark, hover
  BR:    '#9A7430', // bronze accent — italic on cream, eyebrows on cream
  BR2:   '#5A3F18', // mahogany bronze — italic H2 emphasis + eyebrows + labels on cream (deepened May 2026 from #7A5A22)

  // DEPRECATED — gold-deep had no spec equivalent; repoint to BR.
  GOD:   '#9A7430', // DEPRECATED — use BR

  // Text
  INK:   '#1A0F08',
  TX:    '#2C1810',
  TL:    '#3D2E1C',
  TF:    '#7A6040',
  IVY:   '#F0E8D4',

  // Status (untouched — orthogonal to brand ramp)
  GREEN:    '#5A8A5A',
  GREEN_BG: 'rgba(90,138,90,0.08)',
  AMBER:    '#D4961A',
  AMBER_BG: 'rgba(212,150,26,0.08)',
  RED:      '#C0392B',
  RED_BG:   'rgba(192,57,43,0.06)',
};

// ───────── TYPE SYSTEM ─────────
// Editorial = Cormorant Garamond. Cinzel is reserved for the brand wordmark
// ONLY (T.brandMark). All label / eyebrow / day-signpost tokens use FF per
// spec Section 3. Existing exported names are preserved for backwards
// compatibility — values are updated in place to spec, and new spec-named
// tokens are added alongside.
export const T = {
  // Headlines (clamp scale)
  hero:        { fontFamily: FF, fontSize: 'clamp(42px, 6.4vw, 84px)', fontWeight: 400, lineHeight: 1.12, letterSpacing: '-0.008em', margin: 0 },
  display:     { fontFamily: FF, fontSize: 'clamp(40px, 6.4vw, 92px)', fontWeight: 400, lineHeight: 1.08, letterSpacing: '-0.022em', margin: 0 },
  displaySm:   { fontFamily: FF, fontSize: 'clamp(28px, 3.8vw, 48px)', fontWeight: 400, lineHeight: 1.16, letterSpacing: '-0.012em', margin: 0 },

  // Page-level title (Stage 2 / member pages — premise → title → subtitle)
  pageTitle:   { fontFamily: FF, fontSize: 'clamp(34px, 4.8vw, 52px)', fontWeight: 400, lineHeight: 1.16, letterSpacing: '-0.005em', margin: 0 },

  // Italic premise / subtitle / quote
  premise:        { fontFamily: FF, fontSize: 'clamp(22px, 2.7vw, 36px)', fontStyle: 'italic', fontWeight: 400, lineHeight: 1.35, letterSpacing: '-0.005em', margin: 0 },
  subtitle:       { fontFamily: FF, fontSize: 'clamp(20px, 2.3vw, 30px)', fontStyle: 'italic', fontWeight: 400, lineHeight: 1.4,  letterSpacing: '-0.004em', margin: 0 },
  subtitleItalic: { fontFamily: FF, fontSize: 'clamp(20px, 2.2vw, 28px)', fontStyle: 'italic', fontWeight: 500, lineHeight: 1.4,  letterSpacing: '-0.003em', margin: 0 },
  quote:          { fontFamily: FF, fontSize: 'clamp(24px, 2.9vw, 38px)', fontStyle: 'italic', fontWeight: 400, lineHeight: 1.4,  letterSpacing: '-0.008em', margin: 0 },

  // Body prose — Cormorant per spec Section 4.
  bodyLg:      { fontFamily: FF, fontSize: 'clamp(18px, 2.6vw, 24px)', fontWeight: 400, lineHeight: 1.7,  margin: 0 },
  bodyMd:      { fontFamily: FF, fontSize: 'clamp(16px, 1.5vw, 19px)', fontWeight: 400, lineHeight: 1.65, margin: 0 },
  body:        { fontFamily: FF, fontSize: 'clamp(16px, 1.5vw, 19px)', fontWeight: 400, lineHeight: 1.65, margin: 0 }, // spec name; alias of bodyMd
  bodySm:      { fontFamily: FF, fontSize: 'clamp(14px, 1.2vw, 16px)', fontWeight: 400, lineHeight: 1.6,  margin: 0 },

  // Italic body — Cormorant 500 (NOT 400). Cormorant italic 400 on cream
  // reads whisper-weight; same root cause as Session 4's subtitle-italic
  // 400 → 500 bump. Use these instead of `{ ...T.bodySm/bodyMd, fontStyle:
  // 'italic', fontWeight: 500 }` inline overrides. Phase 3 sweep done in
  // Session 7; further callsites should adopt the token directly.
  bodySmItalic: { fontFamily: FF, fontSize: 'clamp(14px, 1.2vw, 16px)', fontWeight: 500, fontStyle: 'italic', lineHeight: 1.6,  margin: 0 },
  bodyMdItalic: { fontFamily: FF, fontSize: 'clamp(16px, 1.5vw, 19px)', fontWeight: 500, fontStyle: 'italic', lineHeight: 1.65, margin: 0 },

  // Content H2 — italic Cormorant, sub-heads inside body prose
  contentH2:   { fontFamily: FF, fontSize: 'clamp(26px, 2.6vw, 34px)', fontStyle: 'italic', fontWeight: 400, lineHeight: 1.3, margin: 0 },

  // Labels & metadata — Cormorant per spec Section 3 (Cinzel reserved for wordmark).
  // Weight 700 (revised May 2026 from 500) — Cormorant at 500 reads too thin
  // for letter-spaced uppercase against saturated cream / sand grounds.
  // Session 4 readability fix (May 2026): eyebrowSm bumped 500 → 700 and
  // letter-spacing aligned to 0.34em so smaller-scale eyebrows read with the
  // same marker-level presence as the large eyebrow. Callers on cream grounds
  // should pair these tokens with P.BR2 (not P.BR) for confident contrast.
  label:       { fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 16px)', fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase', margin: 0 },
  eyebrow:     { fontFamily: FF, fontSize: 'clamp(18px, 1.6vw, 22px)', fontWeight: 700, letterSpacing: '0.34em', textTransform: 'uppercase', margin: 0 },
  eyebrowSm:   { fontFamily: FF, fontSize: 'clamp(11px, 1.05vw, 13px)', fontWeight: 700, letterSpacing: '0.34em', textTransform: 'uppercase', margin: 0 },

  // Day / duration signposts — Cormorant 700 per the same readability fix.
  daylabel:    { fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 16px)', fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase' },

  // Numerals & stats — weight 300 per spec
  numeral:     { fontFamily: FF, fontSize: 'clamp(40px, 5.2vw, 76px)', fontWeight: 300, lineHeight: 1, letterSpacing: '-0.025em' },
  numeralSm:   { fontFamily: FF, fontSize: 'clamp(32px, 4vw, 58px)',   fontWeight: 300, lineHeight: 1, letterSpacing: '-0.025em' },
  statNum:     { fontFamily: FF, fontSize: 'clamp(40px, 4.4vw, 68px)', fontWeight: 300, lineHeight: 1, letterSpacing: '-0.02em' },

  // Brand wordmark — Cinzel ONLY here (footer scale; nav uses an inline override).
  brandMark:   { fontFamily: CINZEL, fontSize: 'clamp(15px, 1.4vw, 18px)', fontWeight: 500, letterSpacing: '0.26em', textTransform: 'uppercase' },

  // UI button tokens by context — system-ui per spec Section 4
  uiButtonNav:  { fontFamily: FS, fontSize: '11px',                     fontWeight: 600, letterSpacing: '0.20em', textTransform: 'uppercase' },
  uiButtonPage: { fontFamily: FS, fontSize: 'clamp(11px, 1vw, 13px)',   fontWeight: 600, letterSpacing: '0.20em', textTransform: 'uppercase' },
  uiButtonHero: { fontFamily: FS, fontSize: '12px',                     fontWeight: 600, letterSpacing: '0.20em', textTransform: 'uppercase' },
  uiButtonLg:   { fontFamily: FS, fontSize: 'clamp(13px, 1.2vw, 15px)', fontWeight: 600, letterSpacing: '0.20em', textTransform: 'uppercase' },

  // Navigation typography
  navLink:      { fontFamily: FF, fontSize: '19px', fontWeight: 500, letterSpacing: '0.01em' },
  dropdownItem: { fontFamily: FF, fontSize: '16px', fontWeight: 400, letterSpacing: '0.005em' },
};

// ───────── INLINE PROSE STYLES (Stage 2 inline-italic / inline-bold) ─────────
// Use as inline overrides. Plain <em> and <strong> are also styled globally
// in src/styles/tokens.css.
// Cream-ground default. Dark sections override inline with P.GO.
// Colour deepened May 2026 (Session 4 readability fix) BR → BR2 so inline
// italic emphasis reads with confident presence on saturated cream / sand.
export const inlineItalic = {
  fontFamily: FF,
  fontStyle: 'italic',
  color: P.BR2,
  fontSize: '1.05em',
};

export const inlineBold = {
  fontWeight: 700,
  color: P.INK,
};

// ───────── LAYOUT TOKENS ─────────
export const SECTION_PADDING   = 'clamp(56px, 7.5vw, 112px) clamp(24px, 4.5vw, 64px)';
export const CONTAINER_MAX     = 1680; // grids & wide layouts
export const CONTAINER_READING = 1100; // centered editorial sections
export const CONTAINER_NARROW  = 880;  // body-text columns

// ───────── ANKH PATHS — canonical Ptolemaic mark per spec Section 9.1 ─────────
// Two paths: loop+crossbar (one continuous shape with the loop hole cut as a
// sub-path via fillRule="evenodd") and shaft. viewBox -16 0 80 174.
// Kept as exported constants for downstream consumers that drew SVG inline;
// new code should import the <Ankh> component from ../components/Ankh.
export const ANKH_LOOP_CROSSBAR = "M 24 0 C 10 0 0 13 0 28 C 0 46 8 64 18 76 C 24 84 28 90 30 96 Q 38 95 46 92 Q 54 89 60 86 Q 64 88 64 92 L 64 108 Q 64 112 60 114 Q 54 111 46 108 Q 38 105 30 104 L 30 118 L 18 118 L 18 104 Q 10 105 2 108 Q -6 111 -12 114 Q -16 112 -16 108 L -16 92 Q -16 88 -12 86 Q -6 89 2 92 Q 10 95 18 96 C 20 90 24 84 30 76 C 40 64 48 46 48 28 C 48 13 38 0 24 0 Z M 24 10 C 15 10 9 20 9 30 C 9 46 18 64 24 68 C 30 64 39 46 39 30 C 39 20 33 10 24 10 Z";
export const ANKH_SHAFT = "M 18 118 Q 16 134 14 150 Q 12 162 10 170 Q 12 174 16 174 L 32 174 Q 36 174 38 170 Q 36 162 34 150 Q 32 134 30 118 Z";

// DEPRECATED — ANKH_PATH was the old viewBox 36×48 mark. Kept as a re-export
// of the canonical loop+crossbar path so legacy `import { ANKH_PATH }` lines
// don't break; the value no longer renders correctly inside the old viewBox
// and any remaining direct path consumer should migrate to <Ankh> instead.
export const ANKH_PATH = ANKH_LOOP_CROSSBAR;

// ───────── ATOMS — ANKH MARKS ─────────
// AnkhMed and AnkhSm now render the canonical Ptolemaic ankh paths.
// The `size` prop is interpreted as HEIGHT in pixels (matching the
// canonical <Ankh> component in src/components/Ankh.jsx).
export function AnkhMed({ color, opacity = 0.9, size = 48 }) {
  var c = color || P.GO;
  var w = Math.round(size * 80 / 174);
  return (
    <svg width={w} height={size} viewBox="-16 0 80 174"
         style={{ opacity: opacity, display: 'block', margin: '0 auto' }}
         aria-hidden="true">
      <path d={ANKH_LOOP_CROSSBAR} fill={c} fillRule="evenodd" />
      <path d={ANKH_SHAFT} fill={c} />
    </svg>
  );
}

export function AnkhSm({ color, opacity = 0.9, size = 28 }) {
  var c = color || P.GO;
  var w = Math.round(size * 80 / 174);
  return (
    <svg width={w} height={size} viewBox="-16 0 80 174"
         style={{ opacity: opacity, display: 'block' }}
         aria-hidden="true">
      <path d={ANKH_LOOP_CROSSBAR} fill={c} fillRule="evenodd" />
      <path d={ANKH_SHAFT} fill={c} />
    </svg>
  );
}

// ───────── ATOMS — EYEBROW (centered = bars on BOTH sides) ─────────
// Default colour is BR2 (deepened May 2026 Session 4 readability fix). Spec
// Section 11 locked eyebrows on cream to BR2 — the atom default is the
// natural place to enforce that so callers don't have to spell it out.
export function Eyebrow({ children, center = false, color }) {
  var c = color || P.BR2;
  var style = T.eyebrow;
  var barStyle = {
    width: 1,
    height: 'clamp(12px, 1.1vw, 16px)',
    background: c,
    opacity: 0.7,
    display: 'inline-block',
    flexShrink: 0,
  };
  return (
    <div style={{
      ...style,
      color: c,
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(10px, 1.1vw, 16px)',
      justifyContent: center ? 'center' : 'flex-start',
    }}>
      <span style={barStyle} />
      <span>{children}</span>
      {center && <span style={barStyle} />}
    </div>
  );
}

// ───────── ATOMS — DROP CAP (legacy; new pages should not use) ─────────
export function DropCap({ children, color }) {
  var c = color || P.BR;
  return (
    <span style={{
      float: 'left', fontFamily: FF, fontSize: 58, lineHeight: 0.86,
      fontWeight: 400, color: c,
      marginRight: 10, marginTop: 6, marginBottom: -4,
    }}>{children}</span>
  );
}

// ───────── ATOMS — GEO DIVIDER (small in-content divider; legacy) ─────────
export function GeoDivider({ color, opacity = 0.55, marginV = 24 }) {
  var c = color || P.GO;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, margin: marginV + 'px 0' }}>
      <div style={{ width: 30, height: 0.5, background: c, opacity: opacity * 0.7 }} />
      <div style={{ width: 5, height: 5, background: c, transform: 'rotate(45deg)', opacity: opacity }} />
      <div style={{ width: 30, height: 0.5, background: c, opacity: opacity * 0.7 }} />
    </div>
  );
}

// ───────── SECTION DIVIDER (major divider between sections) ─────────
export function SectionDivider({ color, onDark = false }) {
  var c = color || (onDark ? P.GO2 : P.GO);
  var ruleOpacity    = onDark ? 0.45 : 0.55;
  var diamondOpacity = onDark ? 0.62 : 0.72;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 14,
      margin: '0 auto',
    }}>
      <div style={{ width: 'clamp(100px, 16vw, 220px)', height: 1, background: c, opacity: ruleOpacity }} />
      <div style={{ width: 12, height: 12, background: c, transform: 'rotate(45deg)', opacity: diamondOpacity }} />
      <div style={{ width: 'clamp(100px, 16vw, 220px)', height: 1, background: c, opacity: ruleOpacity }} />
    </div>
  );
}

// ───────── SECTION HEADLINE (landing-template: roman + italic + accent rule) ─────────
// Use on marketing-style pages (ApproachPage, Landing). For Stage 2 / member
// pages, use PageOpener (below) instead.
export function SectionHeadline({ roman, italic, onDark = false, maxWidth = 1100 }) {
  // Headline italic on cream uses BR2 per spec Section 5 (May 2026 readability
  // fix aligned the atom with the spec; pre-fix it had been P.BR).
  var titleColor    = onDark ? P.IVY : P.INK;
  var italicColor   = onDark ? P.GO2 : P.BR2;
  var accentColor   = onDark ? P.GO2 : P.GO;
  var accentOpacity = onDark ? 0.75 : 0.9;
  return (
    <React.Fragment>
      <h2 style={{
        ...T.display,
        color: titleColor,
        textAlign: 'center',
        margin: '28px auto 32px',
        maxWidth: maxWidth,
      }}>
        <span style={{ display: 'block' }}>{roman}</span>
        {italic && (
          <span style={{ display: 'block', fontStyle: 'italic', color: italicColor, fontSize: '0.9em' }}>{italic}</span>
        )}
      </h2>
      <div style={{
        width: 'clamp(180px, 22vw, 280px)',
        height: 2,
        background: accentColor,
        opacity: accentOpacity,
        margin: '0 auto 40px',
      }} />
    </React.Fragment>
  );
}

// ───────── PAGE OPENER (Stage 2: premise → title → subtitle) ─────────
// The page-level opener pattern for member pages (PrepCurriculum, PrepModule,
// ResultsReveal, ConsultationQuestions, CleoBrief). Premise and subtitle
// optional. Title uses T.display by default; pass titleStyle to override.
export function PageOpener({ premise, title, subtitle, onDark = false, titleStyle, maxWidth = 820 }) {
  // BR2 on cream (deepened May 2026 Session 4 readability fix) keeps the
  // italic premise/subtitle stack readable against saturated SAND/PAP.
  var goldColor  = onDark ? P.GO2 : P.BR2;
  var titleColor = onDark ? P.IVY : P.INK;
  return (
    <div style={{ maxWidth: maxWidth, margin: '0 auto', textAlign: 'center' }}>
      {premise && (
        <p style={{ ...T.premise, color: goldColor, margin: '0 0 20px' }}>{premise}</p>
      )}
      <h1 style={{
        ...T.display,
        color: titleColor,
        margin: 0,
        ...(titleStyle || {}),
      }}>{title}</h1>
      {subtitle && (
        <p style={{ ...T.subtitle, color: goldColor, margin: '24px auto 0', maxWidth: 720 }}>{subtitle}</p>
      )}
    </div>
  );
}

// ───────── MODULE IDENTIFIER (Stage 2: stacked numeral / rule / day label) ─────────
// Used on PrepModule and similar daily-content pages.
//   numeral: Roman numeral or character (48-72px Cormorant)
//   dayLabel: e.g. "Day 1 · 5 min" — 15-17px wt 700 ls 0.3em uppercase
export function ModuleIdentifier({ numeral, dayLabel, onDark = false }) {
  // Day label on cream uses BR2 (deepened May 2026 Session 4 readability fix);
  // numeral keeps BR because its 40-76px scale carries weight 300 without
  // additional bronze deepening.
  var numColor   = onDark ? P.GO2 : P.BR;
  var ruleColor  = onDark ? P.GO  : P.GOD;
  var labelColor = onDark ? P.GO2 : P.BR2;
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 14,
    }}>
      <div style={{ ...T.numeral, color: numColor }}>{numeral}</div>
      <div style={{ width: 40, height: 1, background: ruleColor, opacity: 0.7 }} />
      <div style={{ ...T.daylabel, color: labelColor }}>{dayLabel}</div>
    </div>
  );
}

// ───────── BULLET LIST (Stage 2: 9px diamonds, 36px padding, 22px between) ─────────
export function BulletList({ items, onDark = false, color }) {
  var bulletColor = color || (onDark ? P.GO2 : P.BR);
  var textColor   = onDark ? 'rgba(240,232,212,0.86)' : P.TX;
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map(function(item, i) {
        return (
          <li key={i} style={{
            position: 'relative',
            paddingLeft: 36,
            marginBottom: i === items.length - 1 ? 0 : 22,
            ...T.bodyLg,
            color: textColor,
            textAlign: 'justify',
            hyphens: 'auto',
          }}>
            <span style={{
              position: 'absolute',
              left: 12,
              top: '0.7em',
              width: 9,
              height: 9,
              background: bulletColor,
              transform: 'rotate(45deg)',
            }} />
            {item}
          </li>
        );
      })}
    </ul>
  );
}

// ───────── ERROR STATE (Stage 2: editorial typography; no tiny red eyebrow) ─────────
export function ErrorState({ premise, title, action, onDark = false }) {
  var bg = onDark ? P.OBS : P.SAND;
  // BR2 on cream (deepened May 2026 Session 4 readability fix) so the
  // italic premise reads with confident presence on cream error grounds.
  var goldColor  = onDark ? P.GO2 : P.BR2;
  var titleColor = onDark ? P.IVY : P.INK;
  return (
    <div style={{
      minHeight: '60vh',
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(40px, 6vw, 96px) 24px',
    }}>
      <div style={{ maxWidth: 640, textAlign: 'center' }}>
        {premise && (
          <p style={{ ...T.premise, color: goldColor, margin: '0 0 20px' }}>{premise}</p>
        )}
        <h1 style={{
          ...T.display,
          fontSize: 'clamp(32px, 4.4vw, 56px)',
          color: titleColor,
          margin: '0 0 32px',
        }}>{title}</h1>
        {action}
      </div>
    </div>
  );
}

// ───────── COLONNADE — top + bottom rules with diamond markers ─────────
export function Colonnade({ columns = 3, ruleColor, ruleOpacity = 0.55, diamondColor, diamondSize = 12, children }) {
  var rc = ruleColor || P.GO;
  var dc = diamondColor || rc;
  var positions = Array.from({ length: columns }, function(_, i) {
    return ((100 / columns) * (i + 0.5)) + '%';
  });
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ width: '100%', height: 1, background: rc, opacity: ruleOpacity, position: 'relative' }}>
        {positions.map(function(p, i) {
          return (
            <div key={'tc' + i} style={{
              position: 'absolute', top: -diamondSize / 2, left: p,
              width: diamondSize, height: diamondSize, background: dc,
              transform: 'translateX(-50%) rotate(45deg)',
            }} />
          );
        })}
      </div>
      {children}
      <div style={{ width: '100%', height: 1, background: rc, opacity: ruleOpacity, position: 'relative' }}>
        {positions.map(function(p, i) {
          return (
            <div key={'bc' + i} style={{
              position: 'absolute', top: -diamondSize / 2, left: p,
              width: diamondSize, height: diamondSize, background: dc,
              transform: 'translateX(-50%) rotate(45deg)',
            }} />
          );
        })}
      </div>
    </div>
  );
}

// ───────── BUTTONS ─────────
export function BtnPrimary({ children, onClick, large = false, disabled = false, onDark = false }) {
  var bgIdle = onDark ? P.PAP : P.INK;
  var fgIdle = onDark ? P.INK : P.PAP;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: FS,
        fontSize: large ? 'clamp(12px, 1.1vw, 14px)' : 'clamp(11px, 0.95vw, 12px)',
        fontWeight: 600,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        padding: large
          ? 'clamp(18px, 1.8vw, 22px) clamp(40px, 5vw, 64px)'
          : '14px 32px',
        background: disabled ? 'rgba(26,15,8,0.4)' : bgIdle,
        color: fgIdle,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background 0.25s, color 0.25s',
      }}
      onMouseEnter={function(e) {
        if (disabled) return;
        e.currentTarget.style.background = P.BR;
        e.currentTarget.style.color = P.PAP;
      }}
      onMouseLeave={function(e) {
        if (disabled) return;
        e.currentTarget.style.background = bgIdle;
        e.currentTarget.style.color = fgIdle;
      }}
    >{children}</button>
  );
}

export function BtnGhost({ children, onClick, onDark = false, disabled = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: FS,
        fontSize: 'clamp(11px, 0.95vw, 12px)',
        fontWeight: 600,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        padding: '14px 32px',
        background: 'transparent',
        color: onDark ? P.GO2 : P.BR,
        border: '1px solid ' + (onDark ? P.GO2 : P.GO),
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.25s',
      }}
      onMouseEnter={function(e) {
        if (disabled) return;
        e.currentTarget.style.background = onDark ? 'rgba(210,184,134,0.08)' : 'rgba(181,150,90,0.08)';
      }}
      onMouseLeave={function(e) {
        if (disabled) return;
        e.currentTarget.style.background = 'transparent';
      }}
    >{children}</button>
  );
}

// ───────── PAGE HEADER (legacy small page opener; kept for compat) ─────────
export function PageHeader({ eyebrowText, title, subtitle, onDark = false }) {
  return (
    <div style={{
      textAlign: 'center',
      padding: '56px 24px 32px',
      borderBottom: '0.5px solid ' + (onDark ? 'rgba(210,184,134,0.22)' : 'rgba(154,116,48,0.18)'),
    }}>
      <Eyebrow center color={onDark ? P.GO2 : P.BR}>{eyebrowText}</Eyebrow>
      <h1 style={{
        ...T.displaySm,
        color: onDark ? P.IVY : P.INK,
        marginTop: 22, marginBottom: subtitle ? 12 : 0,
        maxWidth: 720, marginLeft: 'auto', marginRight: 'auto',
      }}>{title}</h1>
      {subtitle && (
        <p style={{
          ...T.bodyMd,
          color: onDark ? 'rgba(240,232,212,0.7)' : P.TL,
          maxWidth: 540, margin: '0 auto',
        }}>{subtitle}</p>
      )}
    </div>
  );
}

// ───────── SPINNER ─────────
export function Spinner({ color, size = 40 }) {
  var c = color || P.GO;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
      <div style={{
        width: size, height: size, borderRadius: '50%',
        border: '2px solid ' + c, borderTopColor: 'transparent',
        animation: 'cleospin 1s linear infinite',
      }} />
      <style>{'@keyframes cleospin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}
